import { Component } from "react";
import { connect } from "react-redux";
import socketIOClient from "socket.io-client";
import { setHostingRoomAC, switchStateAC } from "../../actions/game";
import {
    answerCountUpdate,
    answerStatsRequest,
    answerStatsResponse,
    autoAdvanceScheduled,
    closeQuestion,
    closeRoom,
    gameCompleted,
    generalRankingRequest,
    generalRankingResponse,
    hostGrantInvalid,
    hostRejoinFailed,
    hostRejoinRoom,
    hostRejoinSuccess,
    hostTokenInvalid,
    newQuestion,
    playerJoined,
    playerListUpdate,
    quizStarting,
    roomCreated,
    server,
    userCountUpdate
} from "../../connection/config";
import {
    clearHostGrant,
    clearHostSession,
    getHostRoomCode,
    getHostToken,
    hasHostSession,
    saveHostSession
} from "../../connection/host-auth";
import { MAX_WEB_SOCKET_MESSAGE_SIZE } from "../../utilities/constants";
import CountdownOverlay from "../../components/CountdownOverlay";
import { ConnectionWarning } from "./ConnectionWarning";
import Creating from "./Creating";
import Final from "./Final";
import Question, { TAB_ANSWER_STATS, TAB_REVEAL_ANSWER } from "./Question";
import {
    V_CONNECTION_WARNING,
    V_COUNTDOWN,
    V_CREATING,
    V_FINAL,
    V_QUESTION,
    V_WAITING_FOR_ROOM_CODE,
    V_WAITING_FOR_START
} from "./views";
import WaitingForCode from "./WaitingForCode";
import WaitingForStart from "./WaitingForStart";

// Throttle interval for leaderboard requests (ms)
const LEADERBOARD_THROTTLE_MS = 1000;

class Host extends Component {

    constructor(props) {
        super(props);
        this.state = {
            connectedUsers: 0,
            players: [],              // Player list with avatars for lobby display
            answerCount: 0,
            questions: [],
            questionIndex: -1,
            questionIsOpen: true,
            questionTab: 0,
            answerStats: null,
            generalRanking: null,
            revealAnswer: false,
            revealStats: false,
            hostToken: null,          // Token for host-only actions
            isRejoining: false,       // True while attempting rejoin
            startsAt: null,           // Timestamp when quiz countdown will complete
            hasAutoStartedFirstQuestion: false,  // Guard against duplicate Q1 auto-start
            autoAdvanceAt: null       // Timestamp when auto-advance should trigger
        };

        // Leaderboard throttle state (not in React state to avoid re-renders)
        this._lastLeaderboardRequest = 0;
        this._leaderboardThrottleTimer = null;
        this._leaderboardRequestPending = false;

        this.onReceiveQuestions = this.onReceiveQuestions.bind(this);
        this.onStartQuiz = this.onStartQuiz.bind(this);
        this.nextButton = this.nextButton.bind(this);
        this.onCountdownComplete = this.onCountdownComplete.bind(this);
    }

    /**
     * Get hostToken for emitting host-only events.
     * Reads from state (set on creation) or localStorage (for rejoin).
     */
    getHostToken() {
        return this.state.hostToken || getHostToken();
    }

    componentDidMount() {
        this.socket = socketIOClient(server, {
            closeOnBeforeunload: false,
            maxHttpBufferSize: MAX_WEB_SOCKET_MESSAGE_SIZE
        });

        // Handler for new room creation - now receives { roomCode, hostToken }
        this.socket.on(roomCreated, ({ roomCode, hostToken }) => {
            // Store token in localStorage and state
            saveHostSession(roomCode, hostToken);
            this.setState({ hostToken });
            this.props.setHostingRoom({ ...this.props.game.hostingRoom, roomCode });
            this.props.switchState(V_CONNECTION_WARNING);
        });

        // Handler for successful host rejoin
        // Restores full quiz state including all questions for seamless resume
        this.socket.on(hostRejoinSuccess, roomState => {
            // Extract player list for lobby display (includes avatars)
            const players = (roomState.players || []).map(p => ({
                playerId: p.playerId,
                nickname: p.nickname,
                avatarThumb: p.avatarThumb || null,
                points: p.points || 0
            }));

            const newState = {
                isRejoining: false,
                connectedUsers: players.length,
                players: players,
                questionIndex: roomState.questionIndex,
                answerCount: roomState.answerCount || 0,
                answerStats: roomState.answerStats || null,
                autoAdvanceAt: roomState.autoAdvanceAt || null
            };

            // Hydrate full quiz data if available (allows proceeding to next questions)
            if (roomState.quiz && Array.isArray(roomState.quiz.questions)) {
                newState.questions = roomState.quiz.questions;
                newState.questionIsOpen = roomState.answersOpen;
            }
            // Fallback: hydrate only current question if full quiz not available
            else if (roomState.currentQuestion && roomState.questionIndex >= 0) {
                const questions = [];
                questions[roomState.questionIndex] = roomState.currentQuestion;
                newState.questions = questions;
                newState.questionIsOpen = roomState.answersOpen;
            }

            this.setState(newState);
            this.props.setHostingRoom(roomState);

            // Go to appropriate view based on state
            if (roomState.questionIndex >= 0 && (roomState.currentQuestion || roomState.quiz)) {
                // Quiz in progress - show question view
                this.props.switchState(V_QUESTION);
            } else if (roomState.questionIndex >= 0) {
                // Question started but no current question data - show waiting
                this.props.switchState(V_WAITING_FOR_START);
            } else {
                // Quiz not started yet
                this.props.switchState(V_WAITING_FOR_START);
            }
        });

        // Handler for failed host rejoin
        this.socket.on(hostRejoinFailed, ({ reason }) => {
            console.warn("Host rejoin failed:", reason);
            clearHostSession();
            this.setState({ isRejoining: false });
            this.props.switchState(V_CREATING);
        });

        // Handler for invalid hostToken during any host action
        this.socket.on(hostTokenInvalid, () => {
            console.warn("Host token invalid - session terminated");
            clearHostSession();
            this.setState({ hostToken: null });
        });

        // Handler for invalid hostGrant during room creation
        this.socket.on(hostGrantInvalid, () => {
            console.warn("Host grant invalid - authorization required");
            clearHostGrant();
            // Redirect to main page to re-authenticate
            window.location.hash = "#/";
        });

        // Handler for quiz countdown starting
        this.socket.on(quizStarting, ({ startsAt }) => {
            this.setState({ startsAt, hasAutoStartedFirstQuestion: false });
            this.props.switchState(V_COUNTDOWN);
        });

        this.socket.on(userCountUpdate, count => this.setState({ connectedUsers: count }));

        // Full player list update (used on host rejoin to restore state)
        this.socket.on(playerListUpdate, ({ count, players }) => {
            this.setState({ connectedUsers: count, players: players || [] });
        });

        // Delta update: single player joined or updated (much more efficient than full list)
        this.socket.on(playerJoined, (player) => {
            this.setState(prevState => {
                const existingIndex = prevState.players.findIndex(p => p.playerId === player.playerId);
                let newPlayers;

                if (existingIndex >= 0) {
                    // Update existing player (e.g., avatar changed)
                    newPlayers = [...prevState.players];
                    newPlayers[existingIndex] = {
                        ...newPlayers[existingIndex],
                        nickname: player.nickname,
                        avatarThumb: player.avatarThumb,
                        points: player.points
                    };
                } else {
                    // New player - add to list
                    newPlayers = [...prevState.players, {
                        playerId: player.playerId,
                        nickname: player.nickname,
                        avatarThumb: player.avatarThumb,
                        points: player.points || 0
                    }];
                }

                return {
                    players: newPlayers,
                    connectedUsers: newPlayers.length
                };
            });
        });

        this.socket.on(answerCountUpdate, count => {
            this.setState({ answerCount: count });
            // NOTE: Leaderboard is NOT refreshed here - points only update at round close
            // This keeps the leaderboard stable during answering (no live point changes)
        });
        this.socket.on(answerStatsResponse, stats => this.setState({ answerStats: stats }));
        this.socket.on(generalRankingResponse, stats => this.setState({ generalRanking: stats }));

        // Handler for auto-advance scheduling (when time limit is enabled)
        this.socket.on(autoAdvanceScheduled, ({ autoAdvanceAt }) => {
            this.setState({ autoAdvanceAt });
        });

        this.socket.on(gameCompleted, stats => {
            this.setState({ generalRanking: stats, hostToken: null });
            clearHostSession();  // Clear session when quiz ends
            this.props.switchState(V_FINAL);
        });

        // Check for existing session and attempt rejoin on connect
        if (hasHostSession()) {
            this.setState({ isRejoining: true });
            this.socket.on("connect", () => {
                this.socket.emit(hostRejoinRoom, {
                    roomCode: getHostRoomCode(),
                    hostToken: getHostToken()
                });
            });
        } else {
            this.props.switchState(V_CREATING);
        }
    }

    componentWillUnmount() {
        // Clean up leaderboard throttle timer
        if (this._leaderboardThrottleTimer) {
            clearTimeout(this._leaderboardThrottleTimer);
            this._leaderboardThrottleTimer = null;
        }
        this.socket.disconnect();
    }

    /**
     * Request leaderboard update for live sidebar (internal - actually sends request).
     */
    _doLeaderboardRequest() {
        const roomCode = this.props.game.hostingRoom?.roomCode;
        if (roomCode && this.socket) {
            this._lastLeaderboardRequest = Date.now();
            this._leaderboardRequestPending = false;
            this.socket.emit(generalRankingRequest, roomCode, this.getHostToken());
        }
    }

    /**
     * Request leaderboard update with throttling.
     * At most one request per LEADERBOARD_THROTTLE_MS during answering.
     * Call with immediate=true for key transitions (question open/close/reveal).
     */
    requestLeaderboardUpdate(immediate = false) {
        const now = Date.now();
        const timeSinceLastRequest = now - this._lastLeaderboardRequest;

        // Immediate request (key transitions): bypass throttle
        if (immediate) {
            // Clear any pending throttled request
            if (this._leaderboardThrottleTimer) {
                clearTimeout(this._leaderboardThrottleTimer);
                this._leaderboardThrottleTimer = null;
            }
            this._doLeaderboardRequest();
            return;
        }

        // Throttled request: only proceed if enough time has passed
        if (timeSinceLastRequest >= LEADERBOARD_THROTTLE_MS) {
            this._doLeaderboardRequest();
        } else if (!this._leaderboardRequestPending) {
            // Schedule a request for later to ensure we eventually update
            this._leaderboardRequestPending = true;
            const delay = LEADERBOARD_THROTTLE_MS - timeSinceLastRequest;
            this._leaderboardThrottleTimer = setTimeout(() => {
                this._leaderboardThrottleTimer = null;
                if (this._leaderboardRequestPending) {
                    this._doLeaderboardRequest();
                }
            }, delay);
        }
        // If already pending, do nothing (request will fire when timer expires)
    }

    onReceiveQuestions(questions) {
        this.setState({ questions });
    }

    onStartQuiz() {
        // Emit QUIZ_STARTING to start the 5-second countdown
        this.socket.emit(quizStarting, this.props.game.hostingRoom.roomCode, this.getHostToken());
    }

    onCountdownComplete() {
        // Guard against duplicate triggers
        if (this.state.hasAutoStartedFirstQuestion) {
            return;
        }
        this.setState({ hasAutoStartedFirstQuestion: true, startsAt: null });
        // Start the first question (index 0)
        this.nextQuestion(0);
    }

    nextButton() {
        if (this.state.questionIsOpen && 0 <= this.state.questionIndex) {
            this.setState({ questionIsOpen: false });
            const { imageUrl, ...question } = this.state.questions[this.state.questionIndex];
            // Include hostToken for authorization
            this.socket.emit(closeQuestion, this.props.game.hostingRoom.roomCode, question, this.getHostToken());
        } else {
            const nextIndex = this.state.questionIndex + 1;
            // After rejoin, we may not have the next question - end quiz instead
            if (!this.state.questions[nextIndex]) {
                this.socket.emit(closeRoom, this.props.game.hostingRoom.roomCode, this.getHostToken());
                return;
            }
            this.nextQuestion(nextIndex);
        }
    }

    nextQuestion(index) {
        this.setState({
            questionIndex: index,
            questionIsOpen: true,
            answerCount: 0,
            questionTab: 0,
            revealAnswer: false,
            revealStats: false,
            autoAdvanceAt: null  // Clear any pending auto-advance
        }, () => {
            this.props.switchState(V_QUESTION);
            if (0 <= index) {
                // don't send the image to the server
                const { imageUrl, ...question } = this.state.questions[index];
                // Include hostToken for authorization
                this.socket.emit(newQuestion, this.props.game.hostingRoom.roomCode, { index, ...question }, this.getHostToken());
                // Fetch initial leaderboard data for sidebar (immediate - key transition)
                this.requestLeaderboardUpdate(true);
            }
        });
    }

    isLastQuestion = () => this.state.questionIndex + 1 === this.lastIndexNumber();

    lastIndexNumber = () => {
        if (this.props.game.hostingRoom.questionLimit === 0) {
            return this.state.questions.length;
        } else {
            return Math.min(this.props.game.hostingRoom.questionLimit, this.state.questions.length);
        }
    };

    changeTab = tab => {
        switch (tab) {
            case TAB_REVEAL_ANSWER:
                this.setState({ questionTab: TAB_REVEAL_ANSWER, revealAnswer: true });
                break;
            case TAB_ANSWER_STATS:
                this.setState({ questionTab: TAB_ANSWER_STATS, answerStats: null, revealStats: true });
                break;
            default:
                this.setState({ questionTab: 0 });
        }
    };

    /**
     * Combined reveal action: closes answers, requests stats, and reveals correct answer.
     * Idempotent - safe to call even if already in revealing phase.
     */
    onRevealAnswer = () => {
        const roomCode = this.props.game.hostingRoom.roomCode;

        // Close the question if still open
        if (this.state.questionIsOpen && this.state.questionIndex >= 0) {
            this.setState({ questionIsOpen: false });
            const { imageUrl, ...question } = this.state.questions[this.state.questionIndex];
            this.socket.emit(closeQuestion, roomCode, question, this.getHostToken());
        }

        // Request answer stats
        this.socket.emit(answerStatsRequest, roomCode, this.getHostToken());

        // Fetch final leaderboard state (immediate - key transition)
        this.requestLeaderboardUpdate(true);

        // Set UI to show both answer reveal and stats
        this.setState({
            questionTab: TAB_REVEAL_ANSWER,
            revealAnswer: true,
            revealStats: true
        });
    };

    render() {
        // Show loading while attempting rejoin
        if (this.state.isRejoining) {
            return (<div style={{ textAlign: "center", marginTop: "2rem" }}>Reconnecting to session...</div>);
        }

        // Pass getHostToken to components that emit host-only events
        const hostTokenGetter = () => this.getHostToken();

        switch (this.props.game.state) {
            case V_CREATING:
                return (<Creating {...this.props}
                    socket={this.socket}
                    questionList={this.onReceiveQuestions} />);
            case V_WAITING_FOR_ROOM_CODE:
                return (<WaitingForCode {...this.props} />);
            case V_CONNECTION_WARNING:
                return <ConnectionWarning onConfirmed={() => this.props.switchState(V_WAITING_FOR_START)} />;
            case V_WAITING_FOR_START:
                return (<WaitingForStart {...this.props}
                    socket={this.socket}
                    onStartQuiz={this.onStartQuiz}
                    connectedUsers={this.state.connectedUsers}
                    players={this.state.players}
                    getHostToken={hostTokenGetter} />);
            case V_COUNTDOWN:
                return (<CountdownOverlay
                    {...this.props}
                    socket={this.socket}
                    startsAt={this.state.startsAt}
                    onComplete={this.onCountdownComplete}
                    getHostToken={hostTokenGetter}
                    cancel="Cancel the quiz"
                    closeRoomSignal
                />);
            case V_QUESTION:
                return (<Question {...this.props}
                    socket={this.socket}
                    answerCount={this.state.answerCount}
                    questionIndex={this.state.questionIndex}
                    connectedUsers={this.state.connectedUsers}
                    players={this.state.players}
                    isLastQuestion={this.isLastQuestion()}
                    lastIndexNumber={this.lastIndexNumber()}
                    question={this.state.questions[this.state.questionIndex]}
                    questionIsOpen={this.state.questionIsOpen}
                    questionTab={this.state.questionTab}
                    changeTab={this.changeTab}
                    nextButton={this.nextButton}
                    onRevealAnswer={this.onRevealAnswer}
                    answerStats={this.state.answerStats}
                    revealAnswer={this.state.revealAnswer}
                    revealStats={this.state.revealStats}
                    generalRanking={this.state.generalRanking}
                    autoAdvanceAt={this.state.autoAdvanceAt}
                    getHostToken={hostTokenGetter} />);
            case V_FINAL:
                return (<Final {...this.props}
                    generalRanking={this.state.generalRanking} />);
            default:
                return (<span>NOT FOUND</span>);
        }
    }
}

const mapStateToProps = state => {
    return {
        game: state.game
    };
};

const mapDispatchToProps = dispatch => ({
    switchState: (...args) => dispatch(switchStateAC(...args)),
    setHostingRoom: (...args) => dispatch(setHostingRoomAC(...args))
});

export default connect(mapStateToProps, mapDispatchToProps)(Host);
